// src/pages/Users.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { usersService, companiesService } from "../../services";
import "./Users.css";

// Role options
const ROLE_OPTIONS = [
  { value: '', label: 'All Roles' },
  { value: 'Customer', label: 'Customer' },
  { value: 'Admin', label: 'Admin' },
  { value: 'Moderator', label: 'Moderator' },
  { value: 'Driver', label: 'Driver' },
];

// Status options
const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
];

// Role badge helper
const getRoleClass = (role) => {
  const roleMap = {
    'Admin': 'users-role-admin',
    'Super Admin': 'users-role-admin',
    'Moderator': 'users-role-moderator',
    'Customer': 'users-role-customer',
    'Driver': 'users-role-driver',
    'Guest': 'users-role-guest',
  };
  const roleName = typeof role === 'object' ? role?.name : role;
  return roleMap[roleName] || 'users-role-guest';
};

export default function Users() {
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [stats, setStats] = useState({
    total: 0,
    customers: 0,
    admins: 0,
    drivers: 0,
    active: 0,
    suspended: 0,
  });

  // Filters
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [suspendReason, setSuspendReason] = useState('');

  // Create form
  const [createForm, setCreateForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    role: '',
    companyId: '',
    companyName: '',
    createNewCompany: false,
  });

  // Edit form
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: '',
    companyId: '',
    companyName: '',
    createNewCompany: false,
  });

  const [saving, setSaving] = useState(false);

  // Menu state
  const [activeMenu, setActiveMenu] = useState(null);

  // Fetch users
  const fetchUsers = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const filters = {
        page,
        limit: 10,
        search: search || undefined,
        role: roleFilter || undefined,
        company: companyFilter || undefined,
      };

      // Handle status filter
      if (statusFilter === 'active') {
        filters.isActive = true;
        filters.isSuspended = false;
      } else if (statusFilter === 'inactive') {
        filters.isActive = false;
      } else if (statusFilter === 'suspended') {
        filters.isSuspended = true;
      }

      const response = await usersService.getUsers(filters);

      setUsers(response.users || []);
      setPagination({
        page: response.pagination?.currentPage || page,
        totalPages: response.pagination?.totalPages || 1,
        total: response.pagination?.total || 0,
      });

    } catch (err) {
      console.error('Fetch users error:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, statusFilter, companyFilter]);

  // Fetch stats
  const fetchStats = async () => {
    try {
      const statsData = await usersService.getUserStats();
      setStats({
        total: statsData.total || 0,
        customers: statsData.customers || 0,
        admins: statsData.admins || 0,
        drivers: statsData.drivers || 0,
        active: statsData.active || 0,
        suspended: statsData.suspended || 0,
      });
    } catch (err) {
      console.error('Fetch stats error:', err);
      // Calculate from current page if stats API fails
    }
  };

  // Fetch roles for dropdown
  const fetchRoles = async () => {
    try {
      const rolesData = await usersService.getRoles();
      setRoles(rolesData || []);
    } catch (err) {
      console.error('Fetch roles error:', err);
    }
  };

  // Fetch companies for dropdown
  const fetchCompanies = async () => {
    try {
      const response = await companiesService.getCompanies({ limit: 100, hasClients: true });
      setCompanies(response.companies || []);
    } catch (err) {
      console.error('Fetch companies error:', err);
    }
  };

  // Fetch on mount and when filters change
  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchUsers(1);
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchUsers]);

  // Fetch roles, companies, and stats on mount
  useEffect(() => {
    fetchRoles();
    fetchCompanies();
    fetchStats();
  }, []);

  // Handle toggle status
  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await usersService.toggleStatus(userId, !currentStatus);
      setActiveMenu(null);
      fetchUsers(pagination.page);
      fetchStats();
    } catch (err) {
      console.error('Toggle status error:', err);
      alert('Failed to update user status');
    }
  };

  // Handle suspend user
  const handleSuspendUser = async () => {
    if (!selectedUser) return;

    try {
      setSaving(true);
      await usersService.suspendUser(selectedUser._id, suspendReason);
      setShowSuspendModal(false);
      setSelectedUser(null);
      setSuspendReason('');
      fetchUsers(pagination.page);
      fetchStats();
    } catch (err) {
      console.error('Suspend user error:', err);
      alert('Failed to suspend user');
    } finally {
      setSaving(false);
    }
  };

  // Handle unsuspend user
  const handleUnsuspendUser = async (userId) => {
    if (!window.confirm('Are you sure you want to unsuspend this user?')) return;

    try {
      await usersService.unsuspendUser(userId);
      setActiveMenu(null);
      fetchUsers(pagination.page);
      fetchStats();
    } catch (err) {
      console.error('Unsuspend user error:', err);
      alert('Failed to unsuspend user');
    }
  };

  // Handle delete user
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;

    try {
      await usersService.deleteUser(userId);
      setActiveMenu(null);
      fetchUsers(pagination.page);
      fetchStats();
    } catch (err) {
      console.error('Delete user error:', err);
      alert('Failed to delete user');
    }
  };

  // Handle reset password
  const handleResetPassword = async (userId) => {
    if (!window.confirm('Are you sure you want to reset this user\'s password?')) return;

    try {
      const result = await usersService.resetPassword(userId);
      alert(`Password reset successfully. New password: ${result.temporaryPassword || 'Sent via email'}`);
      setActiveMenu(null);
    } catch (err) {
      console.error('Reset password error:', err);
      alert('Failed to reset password');
    }
  };

  // Handle create user
  const handleCreateUser = async () => {
    if (!createForm.firstName || !createForm.email || !createForm.password || !createForm.role) {
      alert('Please fill in all required fields');
      return;
    }

    // Check if Customer role needs company
    const selectedRole = roles.find(r => r._id === createForm.role);
    if (selectedRole?.name === 'Customer' && !createForm.companyId && !createForm.companyName) {
      alert('Customers require a company. Please select or create one.');
      return;
    }

    try {
      setSaving(true);

      const userData = {
        firstName: createForm.firstName,
        lastName: createForm.lastName,
        email: createForm.email,
        phone: createForm.phone,
        password: createForm.password,
        role: createForm.role,
      };

      // Add company info for customers
      if (selectedRole?.name === 'Customer') {
        if (createForm.createNewCompany && createForm.companyName) {
          userData.companyName = createForm.companyName;
        } else if (createForm.companyId) {
          userData.company = createForm.companyId;
        }
      }

      await usersService.createUser(userData);
      setShowCreateModal(false);
      resetCreateForm();
      fetchUsers(1);
      fetchStats();
      fetchCompanies(); // Refresh companies if new one was created
    } catch (err) {
      console.error('Create user error:', err);
      alert(err.message || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  // Reset create form
  const resetCreateForm = () => {
    setCreateForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      role: '',
      companyId: '',
      companyName: '',
      createNewCompany: false,
    });
  };

  // Open edit modal
  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phone: user.phone || '',
      role: user.role?._id || '',
      companyId: user.company?._id || '',
    });
    setShowEditModal(true);
    setActiveMenu(null);
  };

  // Handle update user
  const handleUpdateUser = async () => {
    if (!selectedUser || !editForm.firstName || !editForm.email) {
      alert('Please fill in all required fields');
      return;
    }

    // Check if new/current role is Customer and needs company
    const selectedRole = roles.find(r => r._id === editForm.role);
    const isCustomer = selectedRole?.name === 'Customer';
    if (isCustomer && !editForm.companyId && !editForm.companyName) {
      alert('Customers require a company. Please select or create one.');
      return;
    }

    try {
      setSaving(true);

      const updates = {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        email: editForm.email,
        phone: editForm.phone,
      };

      // Include role if changed
      if (editForm.role && editForm.role !== selectedUser.role?._id) {
        updates.role = editForm.role;
      }

      // Add company info for customers
      if (isCustomer) {
        if (editForm.createNewCompany && editForm.companyName) {
          updates.companyName = editForm.companyName;
        } else if (editForm.companyId) {
          updates.company = editForm.companyId;
        }
      } else if (selectedUser.role?.name === 'Customer' && !isCustomer) {
        // If changing from Customer to another role, remove company
        updates.company = null;
      }

      // Update user with all data in one call
      await usersService.updateUser(selectedUser._id, updates);

      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers(pagination.page);
      fetchCompanies(); // Refresh companies if new one was created
    } catch (err) {
      console.error('Update user error:', err);
      alert(err.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  // Open suspend modal
  const openSuspendModal = (user) => {
    setSelectedUser(user);
    setSuspendReason('');
    setShowSuspendModal(true);
    setActiveMenu(null);
  };

  // Navigate to user details
  const openUserDetails = (userId) => {
    navigate(`/users/${userId}`);
  };

  // Get role name
  const getRoleName = (role) => {
    if (typeof role === 'object') return role?.name || 'N/A';
    return role || 'N/A';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchUsers(newPage);
    }
  };

  // Check if selected role is Customer (for create form)
  const isCustomerRole = () => {
    const selectedRole = roles.find(r => r._id === createForm.role);
    return selectedRole?.name === 'Customer';
  };

  // Check if edit form role is Customer
  const isEditCustomerRole = () => {
    const selectedRole = roles.find(r => r._id === editForm.role);
    return selectedRole?.name === 'Customer';
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveMenu(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className="users-page-container">
      {/* Header */}
      <div className="mq-welcome-block">
        <button className="mq-back-btn" onClick={() => navigate('/dashboard')}>
          <span className="mq-back-btn-icon">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
              <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          Back Home
        </button>
        <h1 className="mq-welcome-name">Users</h1>
      </div>

      {/* Stats Cards */}
      <div className="users-stats-section">
        <div className="users-stat-card">
          <div className="users-stat-icon total">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="6" r="4" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M2 18C2 14.134 5.582 11 10 11C14.418 11 18 14.134 18 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="users-stat-info">
            <span className="users-stat-value">{stats.total}</span>
            <span className="users-stat-label">Total Users</span>
          </div>
        </div>
        <div className="users-stat-card">
          <div className="users-stat-icon customers">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <rect x="3" y="3" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M7 10L9 12L13 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="users-stat-info">
            <span className="users-stat-value">{stats.customers}</span>
            <span className="users-stat-label">Customers</span>
          </div>
        </div>
        <div className="users-stat-card">
          <div className="users-stat-icon admins">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 2L12.5 7.5H18L13.5 11.5L15.5 17L10 13.5L4.5 17L6.5 11.5L2 7.5H7.5L10 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="users-stat-info">
            <span className="users-stat-value">{stats.admins}</span>
            <span className="users-stat-label">Admins</span>
          </div>
        </div>
        <div className="users-stat-card">
          <div className="users-stat-icon drivers">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M1 13H15V7L19 10V13H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <rect x="1" y="7" width="10" height="6" stroke="currentColor" strokeWidth="1.5"/>
              <circle cx="5" cy="15" r="2" stroke="currentColor" strokeWidth="1.5"/>
              <circle cx="15" cy="15" r="2" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
          </div>
          <div className="users-stat-info">
            <span className="users-stat-value">{stats.drivers}</span>
            <span className="users-stat-label">Drivers</span>
          </div>
        </div>
        <div className="users-stat-card">
          <div className="users-stat-icon active">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M6 10L9 13L14 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="users-stat-info">
            <span className="users-stat-value">{stats.active}</span>
            <span className="users-stat-label">Active</span>
          </div>
        </div>
        <div className="users-stat-card">
          <div className="users-stat-icon suspended">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M7 7L13 13M13 7L7 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="users-stat-info">
            <span className="users-stat-value">{stats.suspended}</span>
            <span className="users-stat-label">Suspended</span>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="users-error">
          <span>{error}</span>
          <button onClick={() => fetchUsers(pagination.page)}>Retry</button>
        </div>
      )}

      {/* Table Card */}
      <div className="users-card-wrapper">
        <div className="users-header-section">
          <div className="users-header-top">
            <h2 className="users-header-title">
              Users {pagination.total > 0 && <span className="users-count">({pagination.total})</span>}
            </h2>

            <div className="users-header-controls">
              {/* Search */}
              <div className="users-search-box">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="6.5" cy="6.5" r="5" stroke="#9ca3af" strokeWidth="1.5"/>
                  <path d="M10 10L14 14" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <input
                  type="text"
                  className="users-search-input"
                  placeholder="Search by name, email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button className="users-search-clear" onClick={() => setSearch('')}>×</button>
                )}
              </div>

              {/* Role Filter */}
              <select
                className="users-filter-select"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                {ROLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>

              {/* Status Filter */}
              <select
                className="users-filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>

              {/* Company Filter */}
              <select
                className="users-filter-select"
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
              >
                <option value="">All Companies</option>
                {companies.map((company) => (
                  <option key={company._id} value={company._id}>{company.name}</option>
                ))}
              </select>

              <button className="users-btn-new" onClick={() => setShowCreateModal(true)}>
                + Add User
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        {loading && users.length === 0 ? (
          <div className="users-loading">
            <div className="users-spinner"></div>
            <p>Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="users-empty">
            <p>No users found</p>
            <small>Try adjusting your filters or add a new user</small>
          </div>
        ) : (
          <table className="users-data-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Company</th>
                <th>Status</th>
                <th>Joined</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user._id} className="users-table-row">
                  <td className="users-col-user" onClick={() => openUserDetails(user._id)}>
                    <div className="users-user-cell">
                      <div className="users-avatar">
                        {user.avatar ? (
                          <img src={user.avatar} alt={user.firstName} />
                        ) : (
                          user.firstName?.[0] || 'U'
                        )}
                      </div>
                      <div className="users-user-info">
                        <span className="users-user-name">
                          {user.firstName} {user.lastName}
                        </span>
                        {user.phone && (
                          <span className="users-user-phone">{user.phone}</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="users-col-email" onClick={() => openUserDetails(user._id)}>
                    <div className="users-email-cell">
                      <span>{user.email}</span>
                      {user.isEmailVerified ? (
                        <span className="users-verified-badge" title="Email Verified">✓</span>
                      ) : (
                        <span className="users-unverified-badge" title="Email Not Verified">!</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`users-role-badge ${getRoleClass(user.role)}`}>
                      {getRoleName(user.role)}
                    </span>
                  </td>
                  <td>
                    {user.company?.name ? (
                      <span className="users-company-name">{user.company.name}</span>
                    ) : (
                      <span className="users-no-company">-</span>
                    )}
                  </td>
                  <td>
                    {user.isSuspended ? (
                      <span className="users-status-badge users-status-suspended">Suspended</span>
                    ) : user.isActive ? (
                      <span className="users-status-badge users-status-active">Active</span>
                    ) : (
                      <span className="users-status-badge users-status-inactive">Inactive</span>
                    )}
                  </td>
                  <td className="users-col-date">{formatDate(user.createdAt)}</td>
                  <td className="users-col-menu">
                    <button
                      className="users-menu-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveMenu(activeMenu === user._id ? null : user._id);
                      }}
                    >
                      ⋮
                    </button>
                    {activeMenu === user._id && (
                      <div className="users-menu-dropdown" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => openUserDetails(user._id)}>View Details</button>
                        <button onClick={() => openEditModal(user)}>Edit User</button>
                        <button onClick={() => handleToggleStatus(user._id, user.isActive)}>
                          {user.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        {user.isSuspended ? (
                          <button onClick={() => handleUnsuspendUser(user._id)}>Unsuspend</button>
                        ) : (
                          <button onClick={() => openSuspendModal(user)}>Suspend</button>
                        )}
                        <button onClick={() => handleResetPassword(user._id)}>Reset Password</button>
                        <button className="users-menu-delete" onClick={() => handleDeleteUser(user._id)}>
                          Delete User
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {users.length > 0 && (
          <div className="users-footer-section">
            <span className="users-footer-text">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <div className="users-pagination-controls">
              <button
                className="users-pagination-btn"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
              >
                Prev
              </button>
              {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    className={`users-pagination-btn ${pagination.page === pageNum ? 'users-pagination-active' : ''}`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                className="users-pagination-btn"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="users-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="users-modal users-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="users-modal-header">
              <h3>Create New User</h3>
              <button className="users-modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <div className="users-modal-body">
              <div className="users-form">
                <div className="users-form-row">
                  <div className="users-form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      value={createForm.firstName}
                      onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
                      placeholder="First name"
                    />
                  </div>
                  <div className="users-form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      value={createForm.lastName}
                      onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
                      placeholder="Last name"
                    />
                  </div>
                </div>

                <div className="users-form-row">
                  <div className="users-form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={createForm.email}
                      onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                      placeholder="email@example.com"
                    />
                  </div>
                  <div className="users-form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={createForm.phone}
                      onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                </div>

                <div className="users-form-row">
                  <div className="users-form-group">
                    <label>Password *</label>
                    <input
                      type="password"
                      value={createForm.password}
                      onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                      placeholder="Minimum 8 characters"
                    />
                  </div>
                  <div className="users-form-group">
                    <label>Role *</label>
                    <select
                      value={createForm.role}
                      onChange={(e) => setCreateForm({
                        ...createForm,
                        role: e.target.value,
                        companyId: '',
                        companyName: '',
                        createNewCompany: false,
                      })}
                    >
                      <option value="">Select Role</option>
                      {roles.map((role) => (
                        <option key={role._id} value={role._id}>{role.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Company Section - Only for Customers */}
                {isCustomerRole() && (
                  <div className="users-form-section">
                    <h4 className="users-form-title">Company Information</h4>
                    <p className="users-form-hint">Customers need a company to access the client portal</p>

                    <div className="users-company-toggle">
                      <label className="users-toggle-label">
                        <input
                          type="checkbox"
                          checked={createForm.createNewCompany}
                          onChange={(e) => setCreateForm({
                            ...createForm,
                            createNewCompany: e.target.checked,
                            companyId: '',
                            companyName: '',
                          })}
                        />
                        <span>Create new company</span>
                      </label>
                    </div>

                    {createForm.createNewCompany ? (
                      <div className="users-form-group">
                        <label>Company Name *</label>
                        <input
                          type="text"
                          value={createForm.companyName}
                          onChange={(e) => setCreateForm({ ...createForm, companyName: e.target.value })}
                          placeholder="Enter new company name"
                        />
                      </div>
                    ) : (
                      <div className="users-form-group">
                        <label>Select Company *</label>
                        <select
                          value={createForm.companyId}
                          onChange={(e) => setCreateForm({ ...createForm, companyId: e.target.value })}
                        >
                          <option value="">Select existing company</option>
                          {companies.map((company) => (
                            <option key={company._id} value={company._id}>{company.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="users-modal-footer">
              <button className="users-btn-outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button
                className="users-btn-primary"
                onClick={handleCreateUser}
                disabled={saving}
              >
                {saving ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="users-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="users-modal users-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="users-modal-header">
              <h3>Edit User</h3>
              <button className="users-modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className="users-modal-body">
              <div className="users-form">
                <div className="users-form-row">
                  <div className="users-form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      value={editForm.firstName}
                      onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                      placeholder="First name"
                    />
                  </div>
                  <div className="users-form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      value={editForm.lastName}
                      onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                      placeholder="Last name"
                    />
                  </div>
                </div>

                <div className="users-form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    placeholder="email@example.com"
                  />
                </div>

                <div className="users-form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    placeholder="+1 (555) 000-0000"
                  />
                </div>

                <div className="users-form-group">
                  <label>Role</label>
                  <select
                    value={editForm.role}
                    onChange={(e) => setEditForm({
                      ...editForm,
                      role: e.target.value,
                      companyId: '',
                      companyName: '',
                      createNewCompany: false,
                    })}
                  >
                    <option value="">Select Role</option>
                    {roles.map((role) => (
                      <option key={role._id} value={role._id}>{role.name}</option>
                    ))}
                  </select>
                </div>

                {/* Company Section - Only for Customers */}
                {isEditCustomerRole() && (
                  <div className="users-form-section">
                    <h4 className="users-form-title">Company Information</h4>
                    <p className="users-form-hint">Customers need a company to access the client portal</p>

                    <div className="users-company-toggle">
                      <label className="users-toggle-label">
                        <input
                          type="checkbox"
                          checked={editForm.createNewCompany}
                          onChange={(e) => setEditForm({
                            ...editForm,
                            createNewCompany: e.target.checked,
                            companyId: e.target.checked ? '' : (selectedUser?.company?._id || ''),
                            companyName: '',
                          })}
                        />
                        <span>Create new company</span>
                      </label>
                    </div>

                    {editForm.createNewCompany ? (
                      <div className="users-form-group">
                        <label>Company Name *</label>
                        <input
                          type="text"
                          value={editForm.companyName}
                          onChange={(e) => setEditForm({ ...editForm, companyName: e.target.value })}
                          placeholder="Enter new company name"
                        />
                      </div>
                    ) : (
                      <div className="users-form-group">
                        <label>Select Company *</label>
                        <select
                          value={editForm.companyId}
                          onChange={(e) => setEditForm({ ...editForm, companyId: e.target.value })}
                        >
                          <option value="">Select existing company</option>
                          {companies.map((company) => (
                            <option key={company._id} value={company._id}>{company.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="users-modal-footer">
              <button className="users-btn-outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
              <button
                className="users-btn-primary"
                onClick={handleUpdateUser}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Suspend User Modal */}
      {showSuspendModal && selectedUser && (
        <div className="users-modal-overlay" onClick={() => setShowSuspendModal(false)}>
          <div className="users-modal" onClick={(e) => e.stopPropagation()}>
            <div className="users-modal-header">
              <h3>Suspend User</h3>
              <button className="users-modal-close" onClick={() => setShowSuspendModal(false)}>×</button>
            </div>
            <div className="users-modal-body">
              <div className="users-suspend-info">
                <p>You are about to suspend:</p>
                <div className="users-suspend-user">
                  <strong>{selectedUser.firstName} {selectedUser.lastName}</strong>
                  <span>{selectedUser.email}</span>
                </div>
                <p className="users-suspend-warning">
                  This user will not be able to log in or access any services until unsuspended.
                </p>
              </div>
              <div className="users-form-group">
                <label>Reason for Suspension</label>
                <textarea
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  placeholder="Enter reason for suspension (optional)"
                  rows="3"
                />
              </div>
            </div>
            <div className="users-modal-footer">
              <button className="users-btn-outline" onClick={() => setShowSuspendModal(false)}>
                Cancel
              </button>
              <button
                className="users-btn-danger"
                onClick={handleSuspendUser}
                disabled={saving}
              >
                {saving ? 'Suspending...' : 'Suspend User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
